import React, { useState, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Box from '../../shared/atoms/Box';
import Text from '../../shared/atoms/Text';
import Button from '../../shared/atoms/Button';
import Link from '../../shared/atoms/Link';
import * as api from '../../../utils/api';
import { currencyFormat } from '../../../utils/utils';
import { mapElementValueToStatus } from '../../rapport/RapportDiagnosticItem';
import AngleDown16 from '../../icons/AngleDown16';
import AngleUp16 from '../../icons/AngleUp16';
import BrandIcon from './BrandIcon';
import { RJ, TITLE_FONT } from '../../rapport/rapportTokens';

const SectionLabel = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.gray.darker};
`;

const Card = styled(Box)`
  background-color: ${RJ.white};
  background-clip: padding-box;
  border: 1px solid ${RJ.border};
  border-radius: ${RJ.radiusLg};
  overflow: hidden;
  margin-bottom: 12px;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${RJ.textSecondary};
  }
`;

// Reset all UA button styling so the header inherits Card's white bg cleanly
// — without `appearance: none` Chromium/Safari render a faint button face
// that bleeds at the rounded corners and looks discoloured against the
// violet border.
const Header = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  appearance: none;
  -webkit-appearance: none;
  background-color: transparent;
  border: none;
  outline: none;
  color: inherit;
  cursor: pointer;
  text-align: left;
  font: inherit;
`;

const IconBubble = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  & svg {
    flex-shrink: 0;
  }
`;

const TitleCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Title = styled.span`
  font-weight: 700;
  color: ${RJ.text};
  font-size: 16px;
`;

const RecoBadge = styled.span`
  /* line-height:1 + symmetric padding centres uppercase glyphs cleanly. */
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 10px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: ${(p) => p.theme.colors.violet.pale};
  color: ${(p) => p.theme.colors.violet.dark};
  white-space: nowrap;
`;

const Subtitle = styled.div`
  margin-top: 2px;
  font-size: 14px;
  color: ${(p) => p.theme.colors.gray.darker};
`;

const Price = styled.div`
  font-family: ${TITLE_FONT};
  font-weight: 900;
  color: ${RJ.blue};
  font-size: 18px;
  white-space: nowrap;

  small {
    font-weight: 600;
    font-size: 11px;
    color: ${RJ.textSecondary};
    margin-left: 3px;
  }
`;

const ChevronWrap = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${(p) => p.theme.colors.gray.darker};
  flex-shrink: 0;
`;

const Body = styled.div`
  padding: 0 20px 20px 20px;
  border-top: 1px solid ${(p) => p.theme.colors.gray.lighter};
`;

const DevisIndicatif = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin: 16px 0;
  background: ${RJ.bg};
  border-left: 3px solid ${RJ.blue};
  border-radius: 8px;
  font-size: 14px;

  & svg {
    flex-shrink: 0;
  }
`;

const SubSection = styled.div`
  margin-top: 18px;
`;

const SubSectionLabel = styled(SectionLabel)`
  display: block;
  margin-bottom: 8px;
`;

const RecoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.gray.lighter};

  &:last-of-type {
    border-bottom: none;
  }
`;

const RecoName = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-right: 12px;
`;

const RecoPrice = styled.div`
  font-weight: 600;
  white-space: nowrap;
`;

const SkeletonRow = styled.div`
  height: 14px;
  background: ${(p) => p.theme.colors.gray.lighter};
  border-radius: 4px;
  margin: 12px 0;

  &:nth-child(2) {
    width: 70%;
  }
  &:nth-child(3) {
    width: 85%;
  }
`;

const ErrorBox = styled.div`
  padding: 14px;
  margin: 16px 0;
  background: ${(p) => p.theme.colors.red.pale};
  color: ${(p) => p.theme.colors.red.dark};
  border-radius: 8px;
  font-size: 14px;
`;

const toNumber = (value) => {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// DEVIS-QA-10 — a velo whose diagnostic is fully OK and whose quote totals 0€
// (no prestation / piece) yields an empty "Devis pour un prochain RDV" card
// that only says "Aucune recommandation…". Hide it. Diagnostic statuses live on
// course.intervention_report (UPPERCASE OK/MONITOR/REPAIR, bridged to the
// summary velo via course_velo_id); the priced total is sous_total_ttc.
const hasActionableItem = (items) =>
  (Array.isArray(items) ? items : []).some((it) => {
    const raw =
      it && it.status != null ? String(it.status).toUpperCase() : null;
    if (raw === 'MONITOR' || raw === 'REPAIR') return true;
    if (raw === 'OK') return false;
    const s = mapElementValueToStatus(it && it.element_value);
    return s === 'to_repair' || s === 'to_monitor';
  });

const isEmptyQuote = (summaryVelo, course) => {
  const subtotal = toNumber(summaryVelo && summaryVelo.sous_total_ttc);
  if (subtotal && subtotal > 0) return false;
  const cvId = summaryVelo && summaryVelo.course_velo_id;
  const ivVelos =
    course &&
    course.intervention_report &&
    Array.isArray(course.intervention_report.velos)
      ? course.intervention_report.velos
      : [];
  const iv =
    cvId != null
      ? ivVelos.find((v) => v && Number(v.course_velo_id) === Number(cvId))
      : null;
  const items =
    iv && Array.isArray(iv.diagnostic_items) ? iv.diagnostic_items : [];
  // Fail open: only hide when we can POSITIVELY confirm the diagnostic is
  // all-OK (items present, none actionable). If it can't be resolved (no
  // intervention_report, unmatched course_velo_id, empty items), SHOW rather
  // than risk silently dropping a velo that actually carries a flagged point.
  if (items.length === 0) return false;
  return !hasActionableItem(items);
};

const resolveToken = (summary, course) => {
  if (summary && summary.token) return summary.token;
  if (course && course.visit_report && course.visit_report.token) {
    return course.visit_report.token;
  }
  return null;
};

// Bridge: visit_report_summary.velos[i].course_velo_id → underlying velo.id,
// which is what rapport.diagnostic_items[].velo_id references.
// Primary path uses intervention_report.velos[] which exposes both
// `course_velo_id` (matches summary) and `velo_id` (matches rapport).
// Fallback: course.velos[] (Laravel pivot may expose `id` either as the
// pivot record id or the related velo.id depending on serialization).
const findRawVeloId = (summaryVelo, course) => {
  if (!summaryVelo || !course) return null;
  const cvId = summaryVelo.course_velo_id;
  if (cvId == null) return null;

  const interventionVelos =
    course.intervention_report &&
    Array.isArray(course.intervention_report.velos)
      ? course.intervention_report.velos
      : null;
  if (interventionVelos) {
    const m = interventionVelos.find(
      (iv) => iv && Number(iv.course_velo_id) === Number(cvId),
    );
    if (m && m.velo_id != null) return m.velo_id;
  }

  if (Array.isArray(course.velos)) {
    // Match against course.velos[].id (pivot id when exposed) OR fall through.
    const cv = course.velos.find((v) => v && Number(v.id) === Number(cvId));
    if (cv && cv.velo_id != null) return cv.velo_id;
  }

  return null;
};

const formatDateLong = (iso, lang) => {
  if (!iso) return null;
  const d = new Date(typeof iso === 'string' ? iso.replace(' ', 'T') : iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
};

const FutureQuoteCard = memo(function FutureQuoteCard({
  velo,
  index,
  rapport,
  loading,
  error,
  onExpandFirstTime,
  course,
  currency,
  token,
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const toggle = () => {
    if (!open && !rapport && !loading) onExpandFirstTime();
    setOpen((v) => !v);
  };

  const subtotal = toNumber(velo && velo.sous_total_ttc);
  const label = (velo && velo.velo_label) || t('course.future.bike.fallback');
  const bikeNumber = index + 1;

  // Filter the fetched rapport down to this velo's items + linked recos.
  // Memoised so the 4 filter passes + Set construction only re-run when the
  // velo bridge inputs (rapport / course / velo) actually change — not on
  // every parent re-render driven by sibling cards' loading state.
  const { prestations, pieces } = useMemo(() => {
    if (!rapport) {
      return { prestations: [], pieces: [] };
    }
    const rawVeloId = findRawVeloId(velo, course);
    const veloItems =
      Array.isArray(rapport.diagnostic_items) && rawVeloId != null
        ? rapport.diagnostic_items.filter(
            (it) => Number(it.velo_id) === Number(rawVeloId),
          )
        : [];
    const itemIds = new Set(veloItems.map((it) => it.id));
    const veloRecos = Array.isArray(rapport.recommendations)
      ? rapport.recommendations.filter((r) => itemIds.has(r.diagnostic_item_id))
      : [];
    return {
      prestations: veloRecos.filter((r) => r.type === 'prestation'),
      pieces: veloRecos.filter((r) => r.type === 'piece'),
    };
  }, [rapport, course, velo]);

  const publishedAt =
    rapport && rapport.visit_report && rapport.visit_report.published_at
      ? rapport.visit_report.published_at
      : null;
  const formattedDate = formatDateLong(publishedAt, i18n.language);

  return (
    <Card data-testid="future-quote-card">
      <Header
        type="button"
        aria-expanded={open}
        onClick={toggle}
        data-testid="future-quote-card-toggle">
        <IconBubble aria-hidden="true">
          <BrandIcon name="devis" size={28} />
        </IconBubble>
        <TitleCol>
          <TitleRow>
            <Title>{t('course.future.card.title')}</Title>
            <RecoBadge>{t('course.future.card.badge')}</RecoBadge>
          </TitleRow>
          <Subtitle>
            {t('course.future.card.subtitle', { number: bikeNumber, label })}
          </Subtitle>
        </TitleCol>
        {subtotal !== null && (
          <Price data-testid="future-quote-card-price">
            {currencyFormat(subtotal, currency)}
            <small>TTC</small>
          </Price>
        )}
        <ChevronWrap>{open ? <AngleUp16 /> : <AngleDown16 />}</ChevronWrap>
      </Header>

      {open && (
        <Body data-testid="future-quote-card-body">
          <DevisIndicatif>
            <BrandIcon name="a-surveiller" size={28} />
            <div>
              <strong>
                {t('course.future.indicatif.title')}
                {formattedDate
                  ? ` — ${t('course.future.indicatif.dated', {
                      date: formattedDate,
                    })}`
                  : ''}
              </strong>
              <div>{t('course.future.indicatif.text')}</div>
            </div>
          </DevisIndicatif>

          {loading && !rapport && (
            <Box data-testid="future-quote-card-skeleton">
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </Box>
          )}

          {error && !loading && (
            <ErrorBox data-testid="future-quote-card-error">
              {t('course.future.error')}
            </ErrorBox>
          )}

          {rapport && prestations.length > 0 && (
            <SubSection>
              <SubSectionLabel>
                {t('course.future.section.prestations')}
              </SubSectionLabel>
              {prestations.map((reco, i) => (
                <RecoRow key={`p-${reco.id}-${i}`}>
                  <RecoName>
                    <span style={{ fontWeight: 500 }}>{reco.name}</span>
                    {reco.description && (
                      <Text mb={0} color="gray.darker" fontSize="sm">
                        {reco.description}
                      </Text>
                    )}
                  </RecoName>
                  <RecoPrice>
                    {currencyFormat(toNumber(reco.price_ttc) || 0, currency)}
                  </RecoPrice>
                </RecoRow>
              ))}
            </SubSection>
          )}

          {rapport && pieces.length > 0 && (
            <SubSection>
              <SubSectionLabel>
                {t('course.future.section.pieces')}
              </SubSectionLabel>
              {pieces.map((reco, i) => (
                <RecoRow key={`pc-${reco.id}-${i}`}>
                  <RecoName>
                    <span style={{ fontWeight: 500 }}>{reco.name}</span>
                  </RecoName>
                  <RecoPrice>
                    {currencyFormat(toNumber(reco.price_ttc) || 0, currency)}
                  </RecoPrice>
                </RecoRow>
              ))}
            </SubSection>
          )}

          {rapport &&
            !loading &&
            prestations.length === 0 &&
            pieces.length === 0 && (
              <Text mt={3} color="gray.darker" fontSize="sm">
                {t('course.future.empty')}
              </Text>
            )}

          {/* DEVIS-QA-25 / ticket #40 — lien violet (secondaire) : le bouton
              primaire « Planifier un RDV » vit dans la sidebar (SideActions).
              Un seul bouton vert par page ; celui-ci devient lien violet. */}
          {token && (
            <Box mt={3}>
              <Link
                as="a"
                color="violet.main"
                href={`/reparation/depuis-devis/${encodeURIComponent(
                  token,
                )}/recap`}
                data-testid="future-quote-card-cta">
                {t('course.future.cta.plan')}
              </Link>
            </Box>
          )}
        </Body>
      )}
    </Card>
  );
});

FutureQuoteCard.propTypes = {
  velo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    course_velo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    velo_label: PropTypes.string,
    sous_total_ttc: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  index: PropTypes.number.isRequired,
  rapport: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  onExpandFirstTime: PropTypes.func.isRequired,
  course: PropTypes.object.isRequired,
  currency: PropTypes.string.isRequired,
  token: PropTypes.string,
};

FutureQuoteCard.defaultProps = {
  rapport: null,
  token: null,
};

const FutureQuoteBlock = ({ course }) => {
  const { t } = useTranslation();
  const summary = course && course.visit_report_summary;
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const token = resolveToken(summary, course);

  const fetchRapport = useCallback(async () => {
    if (rapport || loading || !token) return;
    setLoading(true);
    setError(false);
    try {
      const response = await api.get(`/rapport/${encodeURIComponent(token)}`);
      const data =
        response && response.success === true && response.data
          ? response.data
          : response;
      if (!data || data.error_message || !data.course) {
        setError(true);
      } else {
        setRapport(data);
      }
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token, rapport, loading]);

  if (!summary) return null;
  const velos = Array.isArray(summary.velos) ? summary.velos : [];
  // DEVIS-QA-10 — drop velos with an empty quote (all OK + 0€), keeping each
  // remaining velo's original index so its "Vélo n°X" number stays stable.
  const visibleVelos = velos
    .map((velo, idx) => ({ velo, idx }))
    .filter(({ velo }) => !isEmptyQuote(velo, course));
  if (visibleVelos.length === 0) return null;

  const currency = (course && course.currency) || '€';

  return (
    <Box mb={6} data-testid="future-quote-block">
      <SectionLabel as="div" mb={3}>
        {t('course.future.section.label')}
      </SectionLabel>
      {visibleVelos.map(({ velo, idx }) => (
        <FutureQuoteCard
          key={(velo && (velo.id || velo.course_velo_id)) || `velo-${idx}`}
          velo={velo}
          index={idx}
          rapport={rapport}
          loading={loading}
          error={error}
          onExpandFirstTime={fetchRapport}
          course={course}
          currency={currency}
          token={token}
        />
      ))}
    </Box>
  );
};

FutureQuoteBlock.propTypes = {
  course: PropTypes.shape({
    currency: PropTypes.string,
    visit_report: PropTypes.shape({
      token: PropTypes.string,
    }),
    visit_report_summary: PropTypes.shape({
      token: PropTypes.string,
      published_at: PropTypes.string,
      total_ttc_estimated: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      velos: PropTypes.array,
    }),
    velos: PropTypes.array,
  }),
};

FutureQuoteBlock.defaultProps = {
  course: null,
};

export default FutureQuoteBlock;
